import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import {
  HEADER,
  type ApiResponse,
  type AuthTokens,
} from '@network-verify/shared';
import {
  getAccessToken,
  getRefreshToken,
  setTokens as persistTokens,
  clearAll,
} from '../utils/token';

/**
 * 业务错误异常
 *
 * 由响应拦截器在 code !== 0 或 HTTP 错误时抛出，
 * 调用方通过 `instanceof ApiError` 判断业务错误并读取 code。
 */
export class ApiError extends Error {
  readonly code: number;
  readonly msg: string;
  constructor(code: number, msg: string) {
    super(msg);
    this.name = 'ApiError';
    this.code = code;
    this.msg = msg;
  }
}

/** 鉴权状态变更事件名（供 store 监听以同步内存状态） */
export const AUTH_EVENTS = {
  TOKEN_REFRESHED: 'nv:token-refreshed',
  AUTH_CLEARED: 'nv:auth-cleared',
} as const;

/** 防止并发刷新：401 期间复用同一个 refresh promise */
let refreshPromise: Promise<string> | null = null;

/** 跳转登录页（避免与 router 产生循环依赖，直接用 location） */
function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  const { pathname, search } = window.location;
  if (pathname === '/login') return;
  const from = encodeURIComponent(pathname + search);
  window.location.href = `/login?from=${from}`;
}

/**
 * 用 refresh_token 换取新的 access_token
 *
 * 使用独立的 axios 实例（不走主实例拦截器），
 * 避免 401 时无限递归刷新。
 */
function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise;
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new ApiError(-82005, 'refresh token 不存在'));
  }
  refreshPromise = axios
    .post<ApiResponse<AuthTokens>>('/api/auth/refresh', { refresh_token: refreshToken })
    .then((res) => {
      const body = res.data;
      if (!body || body.code !== 0 || !body.data) {
        throw new ApiError(body?.code ?? -1, body?.msg ?? '刷新 token 失败');
      }
      const { access_token, refresh_token } = body.data;
      persistTokens(access_token, refresh_token);
      window.dispatchEvent(
        new CustomEvent(AUTH_EVENTS.TOKEN_REFRESHED, {
          detail: { access_token, refresh_token },
        }),
      );
      return access_token;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

/** 主 axios 实例 */
const instance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// 请求拦截器：自动携带 Bearer token
instance.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set(HEADER.AUTHORIZATION, `${HEADER.BEARER_PREFIX}${token}`);
  }
  return config;
});

// 响应拦截器：
//   HTTP 200 + code=0 → 返回业务数据（response.data.data）
//   HTTP 200 + code!=0 → reject(ApiError)
//   HTTP 401 → 尝试刷新后重试；失败则清空跳登录
//   其他 HTTP 错误 → reject(ApiError)
instance.interceptors.response.use(
  (response) => {
    const body = response.data as ApiResponse;
    if (body && typeof body.code === 'number' && body.code === 0) {
      // 直接返回业务数据，调用方拿到的就是 data 字段
      return body.data as unknown as AxiosResponse;
    }
    return Promise.reject(new ApiError(body?.code ?? -1, body?.msg ?? '未知错误'));
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async (error: any) => {
    const config = error?.config;
    const status = error?.response?.status as number | undefined;

    // 401 → 刷新 token 并重试原请求
    if (status === 401 && config && !config._retried) {
      config._retried = true;
      try {
        const newToken = await refreshAccessToken();
        config.headers.set(HEADER.AUTHORIZATION, `${HEADER.BEARER_PREFIX}${newToken}`);
        return instance(config);
      } catch {
        clearAll();
        window.dispatchEvent(new CustomEvent(AUTH_EVENTS.AUTH_CLEARED));
        redirectToLogin();
        return Promise.reject(new ApiError(-81003, '登录已过期，请重新登录'));
      }
    }

    // 其他 HTTP 错误：尝试解析业务错误体
    const body = error?.response?.data as ApiResponse | undefined;
    if (body && typeof body.code === 'number') {
      return Promise.reject(new ApiError(body.code, body.msg));
    }
    if (status !== undefined) {
      return Promise.reject(new ApiError(status, error?.message ?? '请求失败'));
    }
    return Promise.reject(
      error instanceof Error ? error : new ApiError(-1, String(error)),
    );
  },
);

/** GET：返回业务数据 */
export function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return instance.get(url, config) as unknown as Promise<T>;
}

/** POST：返回业务数据 */
export function post<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  return instance.post(url, data, config) as unknown as Promise<T>;
}

/** PUT：返回业务数据 */
export function put<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  return instance.put(url, data, config) as unknown as Promise<T>;
}

/** DELETE：返回业务数据 */
export function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return instance.delete(url, config) as unknown as Promise<T>;
}

export default instance;
